<template>
  <div class="bg-white shadow-sm border-b border-gray-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Договоры</h1>
          <p class="mt-1 text-sm text-gray-500">Управление договорами</p>
        </div>
        <button
          @click="openCreateModal"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
        >
          <i class="pi pi-plus mr-2"></i>
          Добавить договор
        </button>
      </div>
    </div>
  </div>
  <div class="container mx-auto px-4 py-8">
    <div class="bg-white shadow-md rounded-lg overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <button
              @click="showFilters = !showFilters"
              class="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors duration-150"
            >
              <i
                class="pi transition-transform duration-200"
                :class="showFilters ? 'pi-chevron-up' : 'pi-chevron-down'"
              ></i>
              <span>Фильтры</span>
              <span
                v-if="activeFiltersCount > 0"
                class="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-blue-600 rounded-full"
              >
                {{ activeFiltersCount }}
              </span>
            </button>
            <div class="flex items-center space-x-2">
              <label class="text-sm font-medium text-gray-700"
                >Сортировка:</label
              >
              <select
                v-model="filters.sort"
                @change="loadContracts"
                class="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="number_of_contract">Номер договора</option>
                <option value="date_of_contract">Дата договора</option>
                <option value="legal_name">Юр. название</option>
                <option value="counterparty_name">Контрагент</option>
                <option value="payment_account_name">Платежный аккаунт</option>
                <option value="payment_account_id">
                  ID платежного аккаунта
                </option>
                <option value="active">Статус</option>
                <option value="is_prepayment">Тип оплаты</option>
                <option value="is_nds">НДС</option>
                <option value="consulting_hourly_rate">Ставка (час)</option>
                <option value="deadline_after_invoice">
                  Срок оплаты после счета
                </option>
                <option value="forecast_payment_after_invoice">
                  Прогноз оплаты после счета
                </option>
                <option value="type_of_purchase">Тип закупки</option>
                <option value="type_organization">Тип организации</option>
              </select>
              <button
                @click="toggleOrder"
                class="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-150"
                :title="
                  filters.order === 'asc' ? 'По возрастанию' : 'По убыванию'
                "
              >
                <i
                  class="pi text-lg transition-all duration-200"
                  :class="
                    filters.order === 'asc' ? 'pi-arrow-up' : 'pi-arrow-down'
                  "
                ></i>
              </button>
            </div>
          </div>
          <button
            @click="resetFilters"
            class="px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors duration-150"
          >
            <i class="pi pi-refresh mr-2"></i>
            Сбросить
          </button>
        </div>
        <div
          v-show="showFilters"
          class="px-6 py-4 border-b border-gray-200 bg-white"
        >
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >ID</label
              >
              <input
                v-model="filters.id"
                @input="debouncedLoadContracts"
                type="number"
                placeholder="ID..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Номер договора</label
              >
              <input
                v-model="filters.number_of_contract"
                @input="debouncedLoadContracts"
                type="text"
                placeholder="Введите номер договора..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Дата договора</label
              >
              <input
                v-model="filters.date_of_contract"
                @change="loadContracts"
                type="date"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Юр. название</label
              >
              <input
                v-model="filters.legal_name"
                @input="debouncedLoadContracts"
                type="text"
                placeholder="Введите юр. название..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Контрагент</label
              >
              <input
                v-model="filters.counterparty_name"
                @input="debouncedLoadContracts"
                type="text"
                placeholder="Введите контрагента..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Платежный аккаунт</label
              >
              <input
                list="payment-accounts-list"
                v-model="filters.payment_account_name"
                @input="debouncedLoadContracts"
                placeholder="Поиск по названию..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <datalist id="payment-accounts-list">
                <option value="">Все аккаунты</option>
                <option
                  v-for="account in paymentAccounts"
                  :key="account.id"
                  :value="account.name"
                >
                  {{ account.name }}
                </option>
              </datalist>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Статус</label
              >
              <select
                v-model="filters.active"
                @change="loadContracts"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Все</option>
                <option :value="true">Активен</option>
                <option :value="false">Неактивен</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >НДС</label
              >
              <select
                v-model="filters.is_nds"
                @change="loadContracts"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Все</option>
                <option :value="true">Да</option>
                <option :value="false">Нет</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Email для документооборота</label
              >
              <input
                v-model="filters.mail_for_document_flow"
                @input="debouncedLoadContracts"
                type="text"
                placeholder="Email..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Контакт для документооборота</label
              >
              <input
                v-model="filters.contact_for_document_flow"
                @input="debouncedLoadContracts"
                type="text"
                placeholder="Контакт..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Валюта</label
              >
              <select
                v-model="filters.currency"
                @change="loadContracts"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Все валюты</option>
                <option
                  v-for="currency in currencies"
                  :key="currency.value"
                  :value="currency.value"
                >
                  {{ currency.label }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Тип закупки</label
              >
              <input
                v-model="filters.type_of_purchase"
                @input="debouncedLoadContracts"
                type="text"
                placeholder="Тип закупки..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Тип организации</label
              >
              <select
                v-model="filters.type_organization"
                @change="loadContracts"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Все типы</option>
                <option
                  v-for="orgType in organizationTypes"
                  :key="orgType.value"
                  :value="orgType.value"
                >
                  {{ orgType.label }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Период оплаты (начало)</label
              >
              <input
                v-model="filters.payment_period_start"
                @change="loadContracts"
                type="date"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Период оплаты (конец)</label
              >
              <input
                v-model="filters.payment_period_end"
                @change="loadContracts"
                type="date"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Период оплаты</label
              >
              <select
                v-model="filters.is_by_month"
                @change="loadContracts"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Любая</option>
                <option :value="true">Помесячная</option>
                <option :value="false">Период</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Ставка (час)</label
              >
              <input
                v-model="filters.consulting_hourly_rate"
                @input="debouncedLoadContracts"
                type="number"
                placeholder="Ставка..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Срок оплаты после счета</label
              >
              <input
                v-model="filters.deadline_after_invoice"
                type="number"
                placeholder="Срок..."
                @input="debouncedLoadContracts"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Прогноз оплаты после счета</label
              >
              <input
                v-model="filters.forecast_payment_after_invoice"
                type="number"
                placeholder="Прогноз..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                @input="debouncedLoadContracts"
              />
            </div>
            <div
              class="col-span-1 sm:col-span-2 md:col-span-4 flex items-center space-x-6 mt-2"
            >
              <div class="flex items-center">
                <input
                  type="checkbox"
                  :checked="filters.is_tariff_by_limit === true"
                  @change="onTariffByLimitChange"
                  class="mr-2"
                  id="tariffByLimitCheckbox"
                />
                <label for="tariffByLimitCheckbox" class="text-sm"
                  >Тариф по лимиту</label
                >
              </div>
              <div class="flex items-center">
                <input
                  type="checkbox"
                  :checked="filters.is_prepayment === true"
                  @change="onPrepaymentChange"
                  class="mr-2"
                  id="prepaymentCheckbox"
                />
                <label for="prepaymentCheckbox" class="text-sm"
                  >Предоплата</label
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading" class="flex justify-center items-center py-12">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
        ></div>
      </div>
      <div
        v-else-if="error"
        class="bg-white rounded-lg shadow-sm border border-red-200 p-6"
      >
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <i class="pi pi-exclamation-triangle text-red-400 text-xl"></i>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Ошибка загрузки</h3>
            <div class="mt-2 text-sm text-red-700">{{ error }}</div>
          </div>
        </div>
      </div>
      <div v-else-if="contracts.length === 0" class="text-center py-12">
        <div
          class="mx-auto h-24 w-24 flex items-center justify-center rounded-full bg-gray-100"
        >
          <i class="pi pi-file-o text-gray-400 text-3xl"></i>
        </div>
        <h3 class="mt-4 text-lg font-medium text-gray-900">Список пуст</h3>
        <p class="mt-2 text-sm text-gray-500">
          Нажмите "Добавить договор", чтобы создать первый договор
        </p>
      </div>
      <div v-else class="bg-white shadow-sm rounded-lg overflow-hidden">
        <div class="overflow-x-auto">
          <table
            class="min-w-full divide-y divide-gray-200"
            style="min-width: 1400px"
          >
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Просмотр
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  ID
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Номер договора
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Дата договора
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Контрагент
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  ID платежного аккаунта
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Платежный аккаунт
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Статус
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Предоплата
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Тип тарифа
                </th>
                <th
                  class="px-6 py-3 text-right text-xs font-medium text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10"
                >
                  Действия
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="contract in contracts"
                :key="contract.id"
                class="hover:bg-blue-50 hover:shadow-sm transition-all duration-150 cursor-pointer group"
                @click="viewContract(contract)"
              >
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <button
                    @click="viewContract(contract)"
                    class="text-gray-600 hover:text-blue-600 group-hover:text-blue-600 transition-colors duration-150"
                    title="Просмотр"
                  >
                    <i class="pi pi-eye"></i>
                  </button>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  {{ contract.id }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  {{ contract.number_of_contract }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  {{ formatDate(contract.date_of_contract) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  {{ contract.counterparty_name }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  {{ contract.payment_account_id }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  {{ contract.payment_account_name }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    :class="{
                      'px-2 py-1 text-xs font-medium rounded-full': true,
                      'bg-green-100 text-green-800': contract.active,
                      'bg-red-100 text-red-800': !contract.active
                    }"
                  >
                    {{ contract.active ? 'Активен' : 'Неактивен' }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    :class="{
                      'px-2 py-1 text-xs font-medium rounded-full': true,
                      'bg-blue-100 text-blue-800': contract.is_prepayment,
                      'bg-gray-100 text-gray-800': !contract.is_prepayment
                    }"
                  >
                    {{ contract.is_prepayment ? 'Предоплата' : 'Постоплата' }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    :class="{
                      'px-2 py-1 text-xs font-medium rounded-full': true,
                      'bg-green-100 text-green-800':
                        contract.is_tariff_by_limit,
                      'bg-orange-100 text-orange-800':
                        !contract.is_tariff_by_limit
                    }"
                  >
                    {{
                      contract.is_tariff_by_limit ? 'Полимитный' : 'Позапросный'
                    }}
                  </span>
                </td>
                <td
                  class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white z-10"
                >
                  <div
                    class="flex items-center justify-end space-x-2"
                    @click.stop
                  >
                    <button
                      @click="openEditModal(contract)"
                      class="text-blue-600 hover:text-blue-900 transition-colors duration-150"
                      title="Редактировать"
                    >
                      <i class="pi pi-pencil"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <span class="text-sm text-gray-500">
                Всего записей: {{ totalCount }}
              </span>
              <div class="flex items-center space-x-2">
                <span class="text-sm text-gray-500">Показать:</span>
                <select
                  v-model="filters.limit"
                  class="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  @change="handleLimitChange"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                </select>
                <span class="text-sm text-gray-500">записей</span>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <button
                @click="exportToCSV"
                :disabled="exportLoading"
                class="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-md hover:bg-green-700 hover:border-green-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i v-if="!exportLoading" class="pi pi-download mr-2"></i>
                <i v-else class="pi pi-spin pi-spinner mr-2"></i>
                {{ exportLoading ? 'Выгрузка...' : 'Выгрузить в CSV' }}
              </button>
              <button
                @click="prevPage"
                :disabled="filters.page <= 1"
                class="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Назад
              </button>
              <span class="text-sm text-gray-500">
                Страница {{ filters.page }} из
                {{ Math.ceil(totalCount / filters.limit) }}
              </span>
              <button
                @click="nextPage"
                :disabled="
                  filters.page >= Math.ceil(totalCount / filters.limit)
                "
                class="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Вперед
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ContractForm
      :show="showModal"
      :mode="modalMode"
      :contract-data="editingContract"
      @close="closeModal"
      @saved="handleFormSaved"
    />
  </div>
</template>

<script setup>
  import { ref, onMounted, computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { useToast } from 'primevue/usetoast'
  import {
    contractApi,
    counterpartyApi,
    paymentAccountApi
  } from '../services/api'
  import api from '../services/api'
  import { CURRENCIES, ORGANIZATION_TYPES } from '../constants'
  import ContractForm from '../components/ContractForm.vue'

  const router = useRouter()
  const toast = useToast()

  const contracts = ref([])
  const counterparties = ref([])
  const paymentAccounts = ref([])
  const loading = ref(false)
  const error = ref('')
  const showModal = ref(false)
  const modalMode = ref('create')
  const totalCount = ref(0)
  const counterpartiesLoading = ref(false)
  const paymentAccountsLoading = ref(false)
  const exportLoading = ref(false)
  const editingContract = ref(null)
  const currencies = CURRENCIES
  const organizationTypes = ORGANIZATION_TYPES

  const showFilters = ref(false)
  const filters = ref({
    id: '',
    number_of_contract: '',
    date_of_contract: '',
    is_prepayment: '',
    deadline_after_invoice: '',
    forecast_payment_after_invoice: '',
    legal_name: '',
    active: true,
    counterparty_id: '',
    counterparty_name: '',
    currency: '',
    is_send_email: '',
    is_nds: '',
    consulting_hourly_rate: '',
    mail_for_document_flow: '',
    contact_for_document_flow: '',
    payment_period_start: '',
    payment_period_end: '',
    type_of_purchase: '',
    is_tariff_by_limit: '',
    type_organization: '',
    payment_account_id: '',
    payment_account_name: '',
    is_by_month: '',
    sort: 'number_of_contract',
    order: 'asc',
    page: 1,
    limit: 100,
    query: ''
  })

  const filterFields = [
    'id',
    'number_of_contract',
    'date_of_contract',
    'is_prepayment',
    'deadline_after_invoice',
    'forecast_payment_after_invoice',
    'legal_name',
    'active',
    'counterparty_id',
    'counterparty_name',
    'currency',
    'is_send_email',
    'is_nds',
    'consulting_hourly_rate',
    'mail_for_document_flow',
    'contact_for_document_flow',
    'payment_period_start',
    'payment_period_end',
    'type_of_purchase',
    'is_tariff_by_limit',
    'type_organization',
    'payment_account_id',
    'payment_account_name',
    'is_by_month'
  ]

  const activeFiltersCount = computed(() => {
    let count = 0
    filterFields.forEach(field => {
      const value = filters.value[field]
      if (value !== '' && value !== null && value !== undefined) {
        count++
      }
    })
    return count
  })

  const loadContracts = async () => {
    loading.value = true
    error.value = ''
    try {
      const params = new URLSearchParams()
      Object.entries(filters.value).forEach(([key, value]) => {
        if (
          value !== '' &&
          value !== null &&
          value !== undefined &&
          !['sort', 'order', 'page', 'limit', 'query'].includes(key)
        ) {
          params.append(`filters[${key}]`, value)
        }
      })
      params.append('limit', filters.value.limit)
      params.append('page', filters.value.page)
      params.append('sort', filters.value.sort)
      params.append('order', filters.value.order)
      if (filters.value.query) params.append('query', filters.value.query)
      const url = `/contract?${params.toString()}`
      const response = await api.get(url)

      contracts.value = response.data.contracts
      totalCount.value = response.data.total_count
    } catch (err) {
      error.value = err.message
      console.error('Error loading contracts:', err)
      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail: err.message,
        life: 3000
      })
    } finally {
      loading.value = false
    }
  }

  const loadCounterparties = async () => {
    counterpartiesLoading.value = true
    try {
      const response = await counterpartyApi.getList()
      if (response && Array.isArray(response)) {
        counterparties.value = response
      } else if (
        response &&
        response.counterparties &&
        Array.isArray(response.counterparties)
      ) {
        counterparties.value = response.counterparties
      } else if (response && response.data && Array.isArray(response.data)) {
        counterparties.value = response.data
      } else {
        counterparties.value = []
      }
    } catch (err) {
      counterparties.value = []
    } finally {
      counterpartiesLoading.value = false
    }
  }

  const loadPaymentAccounts = async () => {
    paymentAccountsLoading.value = true
    try {
      const response = await paymentAccountApi.getList({ limit: 1000 })
      if (response && Array.isArray(response)) {
        paymentAccounts.value = response
      } else if (
        response &&
        response.payment_accounts &&
        Array.isArray(response.payment_accounts)
      ) {
        paymentAccounts.value = response.payment_accounts
      } else if (response && response.data && Array.isArray(response.data)) {
        paymentAccounts.value = response.data
      } else {
        console.warn(
          'Unexpected payment accounts response structure:',
          response
        )
        paymentAccounts.value = []
      }
    } catch (err) {
      paymentAccounts.value = []
    } finally {
      paymentAccountsLoading.value = false
    }
  }

  const openCreateModal = () => {
    modalMode.value = 'create'
    editingContract.value = null
    showModal.value = true
  }

  const openEditModal = contract => {
    modalMode.value = 'edit'
    editingContract.value = contract
    showModal.value = true
  }

  const closeModal = () => {
    showModal.value = false
    editingContract.value = null
  }

  const handleFormSaved = () => {
    loadContracts()
  }

  const viewContract = contract => {
    if (!contract || !contract.id) {
      return
    }

    try {
      router.push(`/contracts/${contract.id}`)
    } catch (error) {
      window.location.href = `/contracts/${contract.id}`
    }
  }

  const formatDate = date => {
    if (!date) return ''
    return new Date(date).toLocaleDateString('ru-RU')
  }

  let searchTimeout
  const debouncedLoadContracts = () => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      filters.value.page = 1
      loadContracts()
    }, 400)
  }

  const prevPage = () => {
    console.log('prevPage called:')
    console.log('Current page:', filters.value.page)

    if (filters.value.page > 1) {
      filters.value.page--
      console.log('Moving to page:', filters.value.page)
      loadContracts()
    } else {
      console.log('Cannot move to prev page - already at first page')
    }
  }

  const nextPage = () => {
    const totalPages = Math.ceil(totalCount.value / filters.value.limit)
    console.log('nextPage called:')
    console.log('Current page:', filters.value.page)
    console.log('Total count:', totalCount.value)
    console.log('Limit:', filters.value.limit)
    console.log('Total pages:', totalPages)

    if (filters.value.page < totalPages) {
      filters.value.page++
      console.log('Moving to page:', filters.value.page)
      loadContracts()
    } else {
      console.log('Cannot move to next page - already at last page')
    }
  }

  const handleLimitChange = () => {
    console.log('handleLimitChange called:')
    console.log('New limit:', filters.value.limit)
    console.log('Resetting page to 1')
    filters.value.page = 1
    loadContracts()
  }

  const resetFilters = () => {
    filters.value = {
      id: '',
      number_of_contract: '',
      date_of_contract: '',
      is_prepayment: '',
      deadline_after_invoice: '',
      forecast_payment_after_invoice: '',
      legal_name: '',
      active: true,
      counterparty_id: '',
      counterparty_name: '',
      currency: '',
      is_send_email: '',
      is_nds: '',
      consulting_hourly_rate: '',
      mail_for_document_flow: '',
      contact_for_document_flow: '',
      payment_period_start: '',
      payment_period_end: '',
      type_of_purchase: '',
      is_tariff_by_limit: '',
      type_organization: '',
      payment_account_id: '',
      payment_account_name: '',
      is_by_month: '',
      sort: 'number_of_contract',
      order: 'asc',
      page: 1,
      limit: 100,
      query: ''
    }
    loadContracts()
  }

  const toggleOrder = () => {
    filters.value.order = filters.value.order === 'asc' ? 'desc' : 'asc'
    loadContracts()
  }

  const onTariffByLimitChange = e => {
    filters.value.is_tariff_by_limit = e.target.checked ? true : null
    loadContracts()
  }
  const onPrepaymentChange = e => {
    filters.value.is_prepayment = e.target.checked ? true : null
    loadContracts()
  }

  const exportToCSV = async () => {
    exportLoading.value = true

    try {
      const params = {
        page: 1,
        limit: 10000,
        sort: filters.value.sort,
        order: filters.value.order
      }

      const filterFields = [
        'id',
        'number_of_contract',
        'date_of_contract',
        'is_prepayment',
        'deadline_after_invoice',
        'forecast_payment_after_invoice',
        'legal_name',
        'active',
        'counterparty_id',
        'counterparty_name',
        'currency',
        'is_send_email',
        'is_nds',
        'consulting_hourly_rate',
        'mail_for_document_flow',
        'contact_for_document_flow',
        'payment_period_start',
        'payment_period_end',
        'type_of_purchase',
        'is_tariff_by_limit',
        'type_organization',
        'payment_account_id',
        'payment_account_name',
        'is_by_month'
      ]

      const filtersObj = {}
      filterFields.forEach(field => {
        const value = filters.value[field]
        if (value !== '' && value !== null && value !== undefined) {
          filtersObj[field] = value
        }
      })

      if (Object.keys(filtersObj).length > 0) {
        params.filters = filtersObj
      }

      const response = await contractApi.download(params)

      if (response) {
        const blob = response

        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `contracts_${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        URL.revokeObjectURL(link.href)
      }

      toast.add({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Файл CSV успешно выгружен',
        life: 3000
      })
    } catch (error) {
      console.error('Ошибка при экспорте CSV:', error)
      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось выгрузить файл CSV',
        life: 3000
      })
    } finally {
      exportLoading.value = false
    }
  }

  onMounted(() => {
    loadContracts()
    loadCounterparties()
    loadPaymentAccounts()
  })
</script>
